import type { FormDefinition2 } from '@gov-nx/core/service';
import { useFormBuilderWithConsent, useLocale } from '@gov-nx/core/service';
import {
	getSelectDefinition,
	resetHours,
	today,
	toStringDate,
	useAutocompleteDefinition,
	useDataboxDefinition,
	useDateDefinition,
	useInputDefinition,
} from '@gov-nx/utils/common';
import type { ServiceCode } from '../definitions/codes';
import type { DataRequest, FormData, FormFields } from './service.types';
import type { RezaOpravneniDto } from '@gov-nx/api';

export interface FormInstanceProps {
	code: ServiceCode;
	isDisabled: boolean;
	isLowAuthorization: boolean;
}

export function FormInstance({
	code,
	isDisabled,
	isLowAuthorization,
}: FormInstanceProps): FormDefinition2<FormData, FormFields> {
	const { t } = useLocale(code);

	const _businessPersonDataBox = getSelectDefinition({
		name: '_businessPersonDataBox',
		isDisabled,
		label: t('form.fields.zadatel-udaje.nazav-po'),
		options: [],
		// provided by ApplicantDetailsSelect
	});

	// _businessAddress does not have form component
	const _businessAddress = useInputDefinition({
		name: '_businessAddress',
		isDisabled,
		label: '',
		placeholder: '',
	});

	const adresatDSId = useAutocompleteDefinition({
		name: 'adresatDSId',
		isDisabled,
		label: t('form.fields.adresat'),
		placeholder: t('form.placeholder.adresat'),
		requiredMessage: t('form.validations.adresat'),
	});

	const datumUcineniUkonu = useDateDefinition({
		name: 'datumUcineniUkonu',
		isDisabled,
		isRequired: true,
		requiredMessage: t('form.validations.datum-ukonu'),
		invalidMessage: t('form.validations.datum-ukonu'),
		label: t('form.fields.datum-ukonu'),
		defaultValue: today(),
		min: {
			min: resetHours(today('2022-02-01')),
			errorMessage: t('form.validations.datum-ukonu-minimum'),
		},
		max: {
			max: today(),
			errorMessage: t('form.validations.datum-ukonu-maximum'),
		},
	});

	const identifikaceUkonu = useInputDefinition({
		name: 'identifikaceUkonu',
		isDisabled,
		isRequired: true,
		isRequiredMessage: t('form.validations.identifikator-ukonu'),
		label: t('form.fields.identifikator-ukonu'),
		placeholder: t('form.placeholder.identifikator-ukonu'),
	});

	const agenda = useAutocompleteDefinition({
		name: 'agenda',
		isDisabled,
		label: t('form.fields.agenda'),
		placeholder: t('form.placeholder.agenda'),
		requiredMessage: t('form.validations.agenda'),
	});

	const jineInformace = useInputDefinition({
		name: 'jineInformace',
		isDisabled,
		label: t('form.fields.jine-informace'),
		placeholder: t('form.placeholder.jine-informace'),
		isMultiline: true,
		rows: 4,
	});

	// ico does not have form component
	const ico = useInputDefinition({
		name: 'ico',
		isDisabled,
		label: '',
		placeholder: '',
	});

	const databoxFields = useDataboxDefinition({
		name: 'datovaSchrankaId',
		isDisabled,
		label: t('form.fields.datova-schranka'),
		types: ['FO', 'PO', 'PFO'],
	});

	const form = useFormBuilderWithConsent<FormData, FormFields>(
		[
			_businessPersonDataBox,
			_businessAddress,

			...databoxFields,
			adresatDSId,

			datumUcineniUkonu,
			identifikaceUkonu,
			agenda,
			jineInformace,
			ico,
		],
		{ isLowAuthorization, isDisabled }
	);

	return form;
}

export const prepareSubmitData =
	(typOsoby: NonNullable<RezaOpravneniDto['zastupujiciOsobaTyp']>) =>
	(data: FormData): DataRequest => ({
		params: {
			adresatDSId: data.adresatDSId?.selected?.datovaSchrankaId,
			naseCisloJednaci: data.naseCisloJednaci,
			naseSpisovaZnacka: data.naseSpisovaZnacka,
			odesilatelDSId: data.datovaSchrankaId,
		},
		body: {
			kodPodani: 'OSVEDCENI_DIGITALNIHO_UKONU',
			// TODO NAKITPO-6263: typOsoby is taken from rezaSwitcher and there is fallback to 'FO' in case there is no activeSwitcherReza.
			// This is needed because BE requires typOsoby in request body, but in case of REZA application, BE should determine typOsoby based on xokz header.
			typOsoby: typOsoby,
			agenda: data.agenda?.selected?.kod,
			datumUcineniUkonu: data.datumUcineniUkonu
				? toStringDate(data.datumUcineniUkonu)
				: undefined,
			identifikaceUkonu: data.identifikaceUkonu,
			jineInformace: data.jineInformace,
			ico: data.ico,
			nazevOvm: data.adresatDSId?.selected?.firma ?? '',
		},
	});
